import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {Link, NavLink, useNavigate, useParams} from "react-router-dom";
import { useSelector } from "react-redux";
import LoadingButton from "../../../src/screens/AttendenceScreen/LoadingButton";
import CustomLoaderSmall from "../../../src/components/Layout/CustomLoaderSmall";
import { useRegisterMutation, useEmployeeRegistrationMutation } from "../../../src/features/userApiSlice";
import {XCircleIcon} from "lucide-react";

const US_STATES = [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" },
];

export default function EmployeeRegistrationScreen() {
    const navigate = useNavigate();

    const { userInfo } = useSelector((state: any) => state.auth);

    const [userRegister, { isLoading }] = useRegisterMutation();
    const [employeeRegister, { isLoading: employeeRegisterLoading } ] = useEmployeeRegistrationMutation();

    // -----------------------------------------
    // Form State
    // -----------------------------------------

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [errorMessage, setErrorMessage] = useState("");

    const {id} = useParams();
    const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{12,}$/;

    const passwordValid = passwordRegex.test(password);

    // -----------------------------------------
    // Redirect if already logged in
    // -----------------------------------------

    useEffect(() => {
        // if (userInfo) {
        //     navigate("/");
        // }
    }, [userInfo, navigate]);

    // -----------------------------------------
    // Submit Handler
    // -----------------------------------------

    const onSubmitHandler = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        setErrorMessage("");

        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match.");
            return;
        }

        if (password.length < 8) {
            setErrorMessage(
                "Password must be at least 8 characters long."
            );
            return;
        }

        try {
            await userRegister({
                firstName: firstName,
                lastName: lastName,
                email: email,
                phoneNumber: phoneNumber,
                passwordHash: password,
            }).unwrap();

            await employeeRegister({
                email: email,
                address: address,
                city: city,
                state: state,
                zip: zip,
            }).unwrap();

            // Navigate to email verification
            navigate(`/management/users/${id}`, {
                state: {
                    email,
                },

            });
        } catch (error: any) {
            const message =
                error?.data?.message ||
                error?.data?.error ||
                error?.message ||
                "Unable to create your account.";

            setErrorMessage(message);
        }
    };

    return (
        <main>
            <Helmet>
                <title>User Registration | K Solution</title>

                <meta
                    name="description"
                    content="Create your K Solution employee account."
                />
            </Helmet>

            <section className="min-h-screen bg-gray-900 py-10 px-4 sm:px-6 lg:px-8">

                <div className="mx-auto max-w-3xl">

                    {/* -------------------------------- */}
                    {/* Header */}
                    {/* -------------------------------- */}

                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mb-8 text-center"
                    >
                        <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                            Create Your Account
                        </h1>

                        <p className="mt-2 text-sm font-medium text-gray-300">
                            Register Employee Account To Get Started
                        </p>
                    </motion.div>

                    {/* -------------------------------- */}
                    {/* Registration Card */}
                    {/* -------------------------------- */}

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="rounded-xl bg-white px-6 py-8 shadow-lg sm:px-10">

                        <form
                            onSubmit={onSubmitHandler}
                            className="space-y-8"
                        >

                            {/* -------------------------------- */}
                            {/* Personal Information */}
                            {/* -------------------------------- */}

                            <section>


                                <div className={"p-1"}>
                                    <NavLink
                                        to={`/management/users/${id}`}
                                    >
                                        <XCircleIcon size={32} color="black" className="gap-3" />
                                    </NavLink>
                                </div>



                                <h2 className="text-xl font-semibold text-gray-900">
                                    Personal Information
                                </h2>

                                <p className="mt-1 text-sm text-gray-500">
                                    Enter your basic employee information.
                                </p>

                                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">

                                    {/* First Name */}

                                    <div>
                                        <label
                                            htmlFor="firstName"
                                            className="block text-sm font-medium text-gray-800"
                                        >
                                            First Name
                                        </label>

                                        <div className="mt-2">
                                            <input
                                                id="firstName"
                                                name="firstName"
                                                type="text"
                                                autoComplete="given-name"
                                                value={firstName}
                                                onChange={(e) =>
                                                    setFirstName(e.target.value)
                                                }
                                                placeholder="Enter your first name"
                                                required
                                                className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                            />
                                        </div>
                                    </div>


                                    {/* Last Name */}

                                    <div>
                                        <label
                                            htmlFor="lastName"
                                            className="block text-sm font-medium text-gray-800"
                                        >
                                            Last Name
                                        </label>

                                        <div className="mt-2">
                                            <input
                                                id="lastName"
                                                name="lastName"
                                                type="text"
                                                autoComplete="family-name"
                                                value={lastName}
                                                onChange={(e) =>
                                                    setLastName(e.target.value)
                                                }
                                                placeholder="Enter your last name"
                                                required
                                                className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                            />
                                        </div>
                                    </div>

                                </div>

                            </section>


                            {/* -------------------------------- */}
                            {/* Contact Information */}
                            {/* -------------------------------- */}

                            <section className="border-t border-gray-200 pt-8">

                                <h2 className="text-xl font-semibold text-gray-900">
                                    Contact Information
                                </h2>

                                <p className="mt-1 text-sm text-gray-500">
                                    We'll use this information to verify your
                                    account.
                                </p>


                                <div className="mt-6 space-y-6">

                                    {/* Email */}

                                    <div>
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-medium text-gray-800"
                                        >
                                            Email Address
                                        </label>

                                        <div className="mt-2">
                                            <input
                                                id="email"
                                                name="email"
                                                type="email"
                                                autoComplete="email"
                                                value={email}
                                                onChange={(e) =>
                                                    setEmail(e.target.value)
                                                }
                                                placeholder="wallybaker@haywardbaker.com"
                                                required
                                                className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                            />
                                        </div>
                                    </div>


                                    {/* Phone */}

                                    <div>
                                        <label
                                            htmlFor="phoneNumber"
                                            className="block text-sm font-medium text-gray-800"
                                        >
                                            Phone Number
                                        </label>

                                        <div className="mt-2">
                                            <input
                                                id="phoneNumber"
                                                name="phoneNumber"
                                                type="tel"
                                                autoComplete="tel"
                                                value={phoneNumber}
                                                onChange={(e) =>
                                                    setPhoneNumber(e.target.value)
                                                }
                                                placeholder="(800) 456-6548"
                                                required
                                                className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="address"
                                            className="block text-sm font-medium text-gray-800"
                                        >
                                            Address
                                        </label>

                                        <div className="mt-2">
                                            <input
                                                id="address"
                                                name="address"
                                                type="tel"
                                                autoComplete="tel"
                                                value={address}
                                                onChange={(e) =>
                                                    setAddress(e.target.value)
                                                }
                                                placeholder="7550 Teague Road, Suite 300
                                                            "
                                                required
                                                className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">

                                        {/* First Name */}

                                        <div>
                                            <label
                                                htmlFor="city"
                                                className="block text-sm font-medium text-gray-800"
                                            >
                                                City
                                            </label>

                                            <div className="mt-2">
                                                <input
                                                    id="city"
                                                    name="city"
                                                    type="text"
                                                    autoComplete="given-name"
                                                    value={city}
                                                    onChange={(e) =>
                                                        setCity(e.target.value)
                                                    }
                                                    placeholder="Hanover"
                                                    required
                                                    className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                                />
                                            </div>
                                        </div>


                                        {/* Last Name */}

                                        <div>
                                            <label
                                                htmlFor="state"
                                                className="block text-sm font-medium text-gray-800"
                                            >
                                                State
                                            </label>

                                            <div className="mt-2">
                                                <select
                                                    id="state"
                                                    name="state"
                                                    value={state}
                                                    onChange={(e) => setState(e.target.value)}
                                                    required
                                                    className="block w-full rounded-md bg-white px-3 py-2.5
            text-base text-gray-900 outline outline-1
            -outline-offset-1 outline-gray-300
            focus:outline-2 focus:-outline-offset-2
            focus:outline-blue-600
            sm:text-sm"
                                                >
                                                    <option value="">
                                                        Select a state
                                                    </option>

                                                    {US_STATES.map((stateItem) => (
                                                        <option
                                                            key={stateItem.code}
                                                            value={stateItem.code}
                                                        >
                                                            {stateItem.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label
                                                htmlFor="state"
                                                className="block text-sm font-medium text-gray-800"
                                            >
                                                Zip Code
                                            </label>

                                            <div className="mt-2">
                                                <input
                                                    id="zipCode"
                                                    name="zipCode"
                                                    type="text"
                                                    autoComplete="family-name"
                                                    value={zip}
                                                    onChange={(e) =>
                                                        setZip(e.target.value)
                                                    }
                                                    placeholder="21076"
                                                    required
                                                    className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                                />
                                            </div>
                                        </div>

                                    </div>

                                </div>

                            </section>


                            {/* -------------------------------- */}
                            {/* Password */}
                            {/* -------------------------------- */}

                            <section className="border-t border-gray-200 pt-8">

                                <h2 className="text-xl font-semibold text-gray-900">
                                    Security
                                </h2>

                                <p className="mt-1 text-sm text-gray-500">
                                    Create a secure password for your account.
                                </p>


                                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">

                                    {/* Password */}

                                    <div>
                                        <label
                                            htmlFor="password"
                                            className="block text-sm font-medium text-gray-800"
                                        >
                                            Password
                                        </label>

                                        <div className="mt-2">
                                            <input
                                                id="password"
                                                name="password"
                                                type="password"
                                                autoComplete="new-password"
                                                value={password}
                                                onChange={(e) =>
                                                    setPassword(e.target.value)
                                                }
                                                placeholder="Create a password"
                                                required
                                                minLength={8}
                                                className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                            />
                                        </div>
                                    </div>


                                    {/* Confirm Password */}

                                    <div>
                                        <label
                                            htmlFor="confirmPassword"
                                            className="block text-sm font-medium text-gray-800"
                                        >
                                            Confirm Password
                                        </label>

                                        <div className="mt-2">
                                            <input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type="password"
                                                autoComplete="new-password"
                                                value={confirmPassword}
                                                onChange={(e) =>
                                                    setConfirmPassword(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Confirm your password"
                                                required
                                                minLength={8}
                                                className="block w-full rounded-md bg-white px-3 py-2.5
                                                text-base text-gray-900 outline outline-1
                                                -outline-offset-1 outline-gray-300
                                                placeholder:text-gray-400
                                                focus:outline-2 focus:-outline-offset-2
                                                focus:outline-blue-600
                                                sm:text-sm"
                                            />
                                        </div>
                                    </div>

                                </div>

                            </section>


                            {/* -------------------------------- */}
                            {/* Error */}
                            {/* -------------------------------- */}

                            {errorMessage && (
                                <motion.div
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="rounded-md bg-red-50 p-4"
                                >
                                    <p className="text-sm font-medium text-red-700">
                                        {errorMessage}
                                    </p>
                                </motion.div>
                            )}

                            {/* -------------------------------- */}
                            {/* Submit Button */}
                            {/* -------------------------------- */}

                            <div className="border-t border-gray-200 pt-6">

                                <LoadingButton isLoading={isLoading || employeeRegisterLoading}
                                type={"submit"} loadingText={"Creating Employee..."}>
                                    Create Employee
                                </LoadingButton>

                            </div>


                        </form>

                    </motion.div>

                </div>

            </section>
        </main>
    );
}